import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { fetchAdminAuditLog } from '../services/api';
import { timeAgo } from '../utils/format';

// Per supabase/migrations/0015_admin_audit_log.sql header. Keep this
// list in sync with KNOWN_ACTIONS in api/_lib/audit.js.
const ACTION_LABEL = {
  approve_user:           'Approved user',
  reject_user:            'Rejected user',
  change_role:            'Changed role',
  set_page_permissions:   'Set page permissions',
  set_notification_subs:  'Set notification subs',
  create_packet:          'Created packet',
  revoke_packet:          'Revoked packet',
  create_pitch_code:      'Created pitch code',
  revoke_pitch_code:      'Revoked pitch code',
  patch_investor:         'Patched investor',
  patch_waitlist_signup:  'Patched waitlist signup',
  patch_support_ticket:   'Patched support ticket',
  patch_careers_ticket:   'Patched careers ticket',
};

const ACTION_TONE = {
  approve_user:           'badge-success',
  reject_user:            'badge-danger',
  change_role:            'badge-warning',
  set_page_permissions:   'badge-warning',
  set_notification_subs:  'badge-info',
  create_packet:          'badge-info',
  revoke_packet:          'badge-danger',
  create_pitch_code:      'badge-info',
  revoke_pitch_code:      'badge-danger',
  patch_investor:         'badge-muted',
  patch_waitlist_signup:  'badge-muted',
  patch_support_ticket:   'badge-muted',
  patch_careers_ticket:   'badge-muted',
};

const MST_TZ = 'America/Edmonton';
const fmtMST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: MST_TZ,
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
};

const AdminActivityLog = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [targetKindFilter, setTargetKindFilter] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (actionFilter) params.action = actionFilter;
      if (targetKindFilter) params.target_kind = targetKindFilter;
      const res = await fetchAdminAuditLog(params);
      setRows(res?.data || []);
      setSummary(res?.summary || null);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, targetKindFilter]);

  useEffect(() => { load(); }, [load]);

  // Action options derived from loaded rows so we don't need a separate
  // call. Empty filter still surfaces every action.
  const actionOptions = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => r.action && seen.add(r.action));
    return Array.from(seen).sort();
  }, [rows]);
  const targetKindOptions = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => r.target_kind && seen.add(r.target_kind));
    return Array.from(seen).sort();
  }, [rows]);

  return (
    <section className="card" style={{ marginTop: 'var(--s5)', padding: 'var(--s5)' }}>
      <header style={{ marginBottom: 'var(--s4)' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--s3)' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', marginBottom: 4 }}>
              Activity log
            </h2>
            <p className="text-xs" style={{ color: 'var(--tx3)', maxWidth: 560 }}>
              Append-only audit trail of every admin action. Captures who, what, when,
              from where. Tamper-resistant — no UPDATE or DELETE policies exist, so even
              a super-admin session cannot mutate or remove rows via the dashboard.
            </p>
          </div>
          {summary && (
            <div className="row row-gap-2" style={{ fontSize: 12 }}>
              <div className="badge badge-muted">Total {summary.total ?? 0}</div>
              <div className="badge badge-info">24h {summary.last_24h ?? 0}</div>
              <div className="badge badge-warning">7d {summary.last_7d ?? 0}</div>
            </div>
          )}
        </div>
      </header>

      <div className="row row-gap-3" style={{ marginBottom: 'var(--s3)', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          className="input"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <option value="">All actions</option>
          {actionOptions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>)}
        </select>
        <select
          className="input"
          value={targetKindFilter}
          onChange={(e) => setTargetKindFilter(e.target.value)}
          style={{ minWidth: 180 }}
        >
          <option value="">All target kinds</option>
          {targetKindOptions.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--s3)' }}>{error}</div>}

      <div className="table-wrap">
        {loading && rows.length === 0 ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">No audit events match this filter yet. Admin actions like approving users, creating packets, or issuing pitch codes will appear here as they happen.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Route</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td title={fmtMST(r.occurred_at)} style={{ whiteSpace: 'nowrap' }}>{timeAgo(r.occurred_at)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.actor_profile?.full_name || r.actor_email?.split('@')[0] || '—'}</div>
                      <div className="text-xs" style={{ color: 'var(--tx3)' }}>{r.actor_email}</div>
                    </td>
                    <td>
                      <span className={clsx('badge', ACTION_TONE[r.action] || 'badge-muted')}>
                        {ACTION_LABEL[r.action] || r.action}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.target_label || r.target_id?.slice(0, 8) || '—'}</div>
                      <div className="text-xs" style={{ color: 'var(--tx3)' }}>{r.target_kind}</div>
                    </td>
                    <td><code style={{ fontSize: 11, color: 'var(--tx3)' }}>{r.api_route}</code></td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--bg-soft)', padding: 'var(--s4)' }}>
                        <div className="text-xs" style={{ color: 'var(--tx3)', marginBottom: 8 }}>
                          {fmtMST(r.occurred_at)}
                          {r.ip ? ` · IP ${r.ip}` : ''}
                          {r.user_agent ? ` · ${r.user_agent.slice(0, 80)}` : ''}
                        </div>
                        <div className="row row-gap-4" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                            <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Before</div>
                            <pre style={{ fontSize: 11, background: 'var(--navy-deep, #0a1520)', color: 'var(--tx1)', padding: 8, borderRadius: 4, margin: 0, overflowX: 'auto' }}>
{JSON.stringify(r.before_state ?? null, null, 2)}
                            </pre>
                          </div>
                          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                            <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>After</div>
                            <pre style={{ fontSize: 11, background: 'var(--navy-deep, #0a1520)', color: 'var(--tx1)', padding: 8, borderRadius: 4, margin: 0, overflowX: 'auto' }}>
{JSON.stringify(r.after_state ?? null, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default AdminActivityLog;
